import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal-page";
import { socialImageMeta, breadcrumbLd } from "@/lib/seo";

export const Route = createFileRoute("/legal/buyer-terms")({
  head: () => ({
    meta: [
      { title: "Buyer Terms — BidNova Auctions" },
      { name: "description", content: "The rules that apply when bidding, buying and paying on BidNova Auctions." },
      { property: "og:title", content: "Buyer Terms — BidNova Auctions" },
      { property: "og:description", content: "The rules that apply when bidding, buying and paying on BidNova Auctions." },
      { property: "og:url", content: "https://bidnovaauctions.uk/legal/buyer-terms" },
      { property: "og:type", content: "article" },
      ...socialImageMeta(),
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/legal/buyer-terms" }],
    scripts: [breadcrumbLd([{ name: "Home", path: "/" }, { name: "Legal", path: "/legal/terms" }, { name: "Buyer Terms", path: "/legal/buyer-terms" }])],
  }),
  component: BuyerTerms,
});

function BuyerTerms() {
  return (
    <LegalPage eyebrow="Legal" title="Buyer Terms" updated="7 July 2026">
      <p>
        These Buyer Terms govern your use of BidNova Auctions ("the platform", "we", "us") when
        placing bids, winning lots and paying for orders. They sit alongside our{" "}
        <a href="/legal/terms">Terms &amp; Conditions</a> and{" "}
        <a href="/legal/privacy">Privacy Policy</a> and are governed by the laws of England &amp; Wales.
      </p>

      <h2>1. Eligibility</h2>
      <ul>
        <li>You must be aged 18 or over and legally resident in the United Kingdom to bid.</li>
        <li>You must provide accurate registration details and keep your account credentials secure.</li>
        <li>You must not operate multiple accounts or bid on behalf of a suspended user.</li>
      </ul>

      <h2>2. Bidding</h2>
      <ul>
        <li>Every bid is a legally binding offer to purchase the lot at the amount bid, subject to any reserve.</li>
        <li>Bids cannot be retracted once placed. Please confirm the amount before submitting.</li>
        <li>Sellers may set a reserve; if bidding does not reach it, no contract of sale is formed.</li>
        <li>Bids placed in the final five (5) minutes of an auction will extend the auction by five (5) minutes ("anti-sniping").</li>
        <li>We may cancel bids that are clearly erroneous, fraudulent, or placed in breach of these terms.</li>
      </ul>

      <h2>3. Winning &amp; Payment</h2>
      <ul>
        <li>If you are the highest bidder when an auction ends and the reserve (if any) is met, you are contractually bound to complete the purchase.</li>
        <li>Payment is due within 72 hours of the auction ending, in pounds sterling (GBP) via our Stripe checkout.</li>
        <li>Non-payment may result in suspension, forfeiture of buyer deposits (if any), and negative feedback.</li>
        <li>The total price shown at checkout includes any applicable UK VAT. Postage is quoted separately.</li>
      </ul>

      <h2>4. Delivery</h2>
      <ul>
        <li>Sellers must dispatch within the timeframe stated on the listing (typically 3 working days after payment).</li>
        <li>You will be notified by email once the seller uploads a tracking number.</li>
        <li>You must confirm delivery within 7 days of receipt so the seller can be paid. If you do not confirm and do not open a dispute, delivery will be deemed confirmed after 14 days.</li>
      </ul>

      <h2>5. Consumer Rights</h2>
      <p>
        Where you buy from a UK trader (not another consumer), the Consumer Contracts Regulations 2013 grant you a
        14-day cancellation right from delivery. This right does <strong>not</strong> apply to items won at public
        auction from private sellers. Your rights under the Consumer Rights Act 2015 (goods must match description and be of
        satisfactory quality) always apply.
      </p>

      <h2>6. Buyer Protection</h2>
      <p>
        Funds are held by the platform until you confirm delivery. If the item is not as described, damaged, or
        never arrives, open a dispute within 7 days and we will investigate under our{" "}
        <a href="/legal/refunds">Refund &amp; Dispute Policy</a>.
      </p>

      <h2>7. Prohibited Conduct</h2>
      <ul>
        <li>Shill bidding, bid retraction abuse, or collusion.</li>
        <li>Contacting sellers to complete transactions off-platform.</li>
        <li>Chargeback abuse — misuse of card-issuer chargebacks in place of the dispute process may result in permanent suspension and recovery action.</li>
      </ul>

      <h2>8. Suspension &amp; Termination</h2>
      <p>We may suspend or terminate your account for breach of these terms, non-payment, or fraudulent activity.</p>

      <h2>9. Contact</h2>
      <p>Questions? Email <a href="mailto:support@bidnovaauctions.uk">support@bidnovaauctions.uk</a>.</p>
    </LegalPage>
  );
}
